import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Search, Plus, MapPin, Phone, Mail, Users,
  ChevronRight, ArrowUpDown, Filter, MoreHorizontal, Eye, Trash2, Edit
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  'Établissement public': 'bg-blue-50 text-blue-700 border-blue-200',
  'Établissement privé': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'OPCO': 'bg-amber-50 text-amber-700 border-amber-200',
  'Université': 'bg-violet-50 text-violet-700 border-violet-200',
  'Association': 'bg-rose-50 text-rose-700 border-rose-200',
  'Entreprise': 'bg-slate-50 text-slate-700 border-slate-200',
};

const StatCard = ({ label, value, accent }) => (
  <div className="flex-1 px-6 py-5" data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <span className="text-sm text-slate-500 block">{label}</span>
    <span className={`text-2xl font-black mt-0.5 block ${accent || 'text-slate-900'}`}>{value}</span>
  </div>
);

const EntreprisesPage = () => {
  const navigate = useNavigate();
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [openMenu, setOpenMenu] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/entreprises`, { credentials: 'include' });
      if (res.ok) setEntreprises(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette entreprise ?')) return;
    await fetch(`${API}/entreprises/${id}`, { method: 'DELETE', credentials: 'include' });
    toast.success('Entreprise supprimée');
    setOpenMenu(null);
    fetchData();
  };

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const types = [...new Set(entreprises.map(e => e.type).filter(Boolean))];

  const filtered = entreprises
    .filter(e => {
      const q = searchQuery.toLowerCase();
      const matchSearch = !q ||
        e.name?.toLowerCase().includes(q) ||
        e.city?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.contact_name?.toLowerCase().includes(q);
      const matchType = !filterType || e.type === filterType;
      return matchSearch && matchType;
    })
    .sort((a, b) => {
      const aVal = (a[sortField] || '').toString().toLowerCase();
      const bVal = (b[sortField] || '').toString().toLowerCase();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

  const totalActive = entreprises.filter(e => e.status === 'active').length;
  const totalLearners = entreprises.reduce((s, e) => s + (e.learner_count || 0), 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="entreprises-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="breadcrumb">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">Entreprises</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">Entreprises</h1>
          <Button
            className="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => navigate('/donnees/entreprises/nouveau')}
            data-testid="add-entreprise-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Nouvelle entreprise
          </Button>
        </div>

        {/* Stats */}
        <div className="bg-violet-50/70 rounded-2xl flex divide-x divide-violet-100" data-testid="stats-bar">
          <StatCard label="Total entreprises" value={entreprises.length} />
          <StatCard label="Actives" value={totalActive} accent="text-emerald-600" />
          <StatCard label="Apprenants liés" value={totalLearners} accent="text-violet-600" />
          <StatCard label="Types distincts" value={types.length} />
        </div>

        {/* Search + Filters */}
        <div className="flex items-center gap-3" data-testid="search-filter-bar">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, ville, email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-entreprises"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <select
              className="pl-10 pr-8 h-10 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none appearance-none cursor-pointer"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              data-testid="filter-type"
            >
              <option value="">Tous les types</option>
              {types.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="entreprises-table">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80">
                <th className="text-left px-6 py-3.5">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700" data-testid="sort-name">
                    Entreprise <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3.5">
                  <button onClick={() => toggleSort('type')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700" data-testid="sort-type">
                    Type <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-left px-4 py-3.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</span>
                </th>
                <th className="text-left px-4 py-3.5">
                  <button onClick={() => toggleSort('city')} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700" data-testid="sort-city">
                    Ville <ArrowUpDown className="h-3 w-3" />
                  </button>
                </th>
                <th className="text-center px-4 py-3.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Apprenants</span>
                </th>
                <th className="text-center px-4 py-3.5">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Statut</span>
                </th>
                <th className="w-12 px-4 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={7} className="px-6 py-4"><div className="h-10 bg-slate-100 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <Building2 className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="text-sm text-slate-400 font-medium">Aucune entreprise trouvée</p>
                    <p className="text-xs text-slate-400 mt-1">Modifiez vos filtres ou créez une nouvelle entreprise</p>
                  </td>
                </tr>
              ) : (
                filtered.map(ent => (
                  <tr
                    key={ent.entreprise_id}
                    className="hover:bg-violet-50/30 cursor-pointer transition-colors"
                    onClick={() => navigate(`/donnees/entreprises/${ent.entreprise_id}`)}
                    data-testid={`entreprise-row-${ent.entreprise_id}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 text-violet-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{ent.name}</p>
                          {ent.email && <p className="text-xs text-slate-400 truncate flex items-center gap-1"><Mail className="h-3 w-3 shrink-0" />{ent.email}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant="outline" className={`text-xs ${TYPE_COLORS[ent.type] || 'bg-slate-50 text-slate-600'}`}>
                        {ent.type || '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-slate-700">{ent.contact_name || '—'}</p>
                      {ent.phone && <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3 shrink-0" />{ent.phone}</p>}
                    </td>
                    <td className="px-4 py-4">
                      <span className="flex items-center gap-1.5 text-sm text-slate-600">
                        <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />{ent.city || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-slate-700">
                        <Users className="h-3.5 w-3.5 text-slate-400" />{ent.learner_count ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge className={`text-xs ${ent.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {ent.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div className="relative">
                        <button
                          onClick={e => { e.stopPropagation(); setOpenMenu(openMenu === ent.entreprise_id ? null : ent.entreprise_id); }}
                          className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
                          data-testid={`menu-btn-${ent.entreprise_id}`}
                        >
                          <MoreHorizontal className="h-4 w-4 text-slate-400" />
                        </button>
                        {openMenu === ent.entreprise_id && (
                          <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 z-20 w-44" data-testid={`menu-dropdown-${ent.entreprise_id}`}>
                            <button
                              onClick={e => { e.stopPropagation(); navigate(`/donnees/entreprises/${ent.entreprise_id}`); }}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-violet-50 hover:text-violet-700 transition-colors"
                              data-testid={`view-btn-${ent.entreprise_id}`}
                            >
                              <Eye className="h-4 w-4" /> Voir / Modifier
                            </button>
                            <button
                              onClick={e => handleDelete(ent.entreprise_id, e)}
                              className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                              data-testid={`delete-btn-${ent.entreprise_id}`}
                            >
                              <Trash2 className="h-4 w-4" /> Supprimer
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Footer */}
          {!loading && filtered.length > 0 && (
            <div className="px-6 py-3 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between" data-testid="table-footer">
              <span className="text-xs text-slate-400">{filtered.length} entreprise{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EntreprisesPage;
